import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, TextField, Alert, Typography, MenuItem, Grid, FormControlLabel, Switch,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import {
  SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton, STATUS_FILTER_OPTIONS,
} from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { SubscriptionPlanGrid } from '../../components/superadmin/SubscriptionPlanCard';
import { useLocale } from '../../contexts/LocaleContext';
import { RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const emptyForm = {
  tenantId: '',
  planName: '',
  billingCycle: 'monthly',
  price: 0,
  gstEnabled: true,
  gstRate: 18,
  status: 'active',
};

export const SubscriptionsPage: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detailRow, setDetailRow] = useState<any>(null);
  const { formatCurrency, formatDate } = useLocale();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [subs, tenantRes, planRes] = await Promise.all([
        superAdminAPI.getSubscriptions(),
        superAdminAPI.getTenants(),
        superAdminAPI.getPlans(),
      ]);
      setSubscriptions(subs.data);
      setTenants(tenantRes.data.filter((t: any) => t.status === 'active'));
      setPlans(planRes.data.filter((p: any) => p.status === 'active'));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (sub: any) => {
    setForm({
      tenantId: sub.tenantId,
      planName: sub.planName,
      billingCycle: sub.billingCycle,
      price: sub.price,
      gstEnabled: sub.gstEnabled,
      gstRate: sub.gstRate,
      status: sub.status,
    });
    setOpen(true);
  };

  const handlePlanChange = (planName: string) => {
    const plan = plans.find((p) => p.planName === planName);
    const price = plan
      ? (form.billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice)
      : form.price;
    setForm((f) => ({ ...f, planName, price }));
  };

  const handleCycleChange = (billingCycle: string) => {
    const plan = plans.find((p) => p.planName === form.planName);
    const price = plan
      ? (billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice)
      : form.price;
    setForm((f) => ({ ...f, billingCycle, price }));
  };

  const handleSave = async () => {
    try {
      await superAdminAPI.upsertSubscription(form);
      setMessage('Subscription saved');
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save subscription'));
    }
  };

  const buildSubscriptionFields = (s: any): SuperAdminDetailField[] => [
    { label: 'Tenant', value: s.companyName },
    { label: 'Tenant ID', value: s.tenantId },
    { label: 'Plan', value: s.planName },
    { label: 'Billing Cycle', value: s.billingCycle === 'yearly' ? 'Yearly' : 'Monthly' },
    { label: 'Price', value: formatCurrency(s.price) },
    { label: 'GST', value: s.gstEnabled ? `${s.gstRate}%` : 'Off' },
    { label: 'Next Billing', value: s.nextBillingDate ? formatDate(s.nextBillingDate) : '—' },
    { label: 'Subscription ID', value: String(s._id || ''), fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader
        title="Tenant Subscriptions"
        subtitle={getPageSubtitle('subscriptions')}
        icon={<AutorenewIcon />}

        action={
          <Button variant="contained" onClick={() => { setForm(emptyForm); setOpen(true); }} sx={catchyPrimaryButtonSx}>
            Add / Edit Subscription
          </Button>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <SuperAdminDataTable
        rows={subscriptions}
        loading={loading}
        error={error}
        exportFileName="super-admin-subscriptions"
        exportSheetName="Subscriptions"
        statusField="status"
        searchPlaceholder="Search tenant or plan..."
        statusOptions={[
          { label: 'All Status', value: 'all' },
          { label: 'Active', value: 'active' },
          { label: 'Suspended', value: 'suspended' },
        ]}
        columns={[
          {
            id: 'tenant',
            header: 'Tenant',
            searchValue: (s) => `${s.companyName} ${s.tenantId}`,
            exportValue: (s) => s.companyName,
            render: (s) => <Typography fontWeight={600} fontSize={13}>{s.companyName}</Typography>,
          },
          {
            id: 'plan',
            header: 'Plan',
            searchValue: (s) => s.planName,
            exportValue: (s) => s.planName,
            render: (s) => s.planName,
          },
          {
            id: 'cycle',
            header: 'Cycle',
            searchValue: (s) => s.billingCycle,
            exportValue: (s) => s.billingCycle,
            filterType: 'select',
            filterOptions: [{ label: 'Monthly', value: 'monthly' }, { label: 'Yearly', value: 'yearly' }],
            filterValue: (s) => s.billingCycle,
            render: (s) => s.billingCycle,
          },
          {
            id: 'price',
            header: 'Price',
            searchValue: (s) => String(s.price),
            exportValue: (s) => s.price,
            render: (s) => formatCurrency(s.price),
          },
          {
            id: 'gst',
            header: 'GST',
            exportValue: (s) => s.gstEnabled ? `${s.gstRate}%` : 'Off',
            filterType: 'select',
            filterOptions: [{ label: 'GST On', value: 'true' }, { label: 'GST Off', value: 'false' }],
            filterValue: (s) => String(s.gstEnabled),
            render: (s) => s.gstEnabled ? `${s.gstRate}%` : 'Off',
          },
          {
            id: 'next',
            header: 'Next Billing',
            searchValue: (s) => s.nextBillingDate ? String(s.nextBillingDate) : '',
            exportValue: (s) => s.nextBillingDate,
            render: (s) => formatDate(s.nextBillingDate),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (s) => s.status,
            filterType: 'select',
            filterOptions: STATUS_FILTER_OPTIONS,
            filterValue: (s) => s.status,
            render: (s) => statusChip(s.status),
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(s) => (
          <AdminTableEditButton onClick={() => openEdit(s)} title="Edit subscription" />
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Subscription"
        title={detailRow?.companyName || ''}
        subtitle={detailRow?.planName}
        status={detailRow?.status}
        fields={detailRow ? buildSubscriptionFields(detailRow) : []}
        primaryActions={detailRow ? (
          <Button variant="contained" onClick={() => { openEdit(detailRow); setDetailRow(null); }} sx={catchyPrimaryButtonSx}>
            Edit Subscription
          </Button>
        ) : undefined}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Tenant Subscription</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>Assign plan, billing cycle and GST</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Tenant" value={form.tenantId}
                onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
                sx={catchyTextFieldSx}>
                {tenants.map((t) => (
                  <MenuItem key={t.tenantId} value={t.tenantId}>{t.companyName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mb={1}>PLAN</Typography>
              <SubscriptionPlanGrid
                plans={plans}
                formatMoney={formatCurrency}
                selectedPlanName={form.planName}
                onSelect={(p) => handlePlanChange(p.planName)}
                compact
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Billing Cycle" value={form.billingCycle}
                onChange={(e) => handleCycleChange(e.target.value)} sx={catchyTextFieldSx}>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Price" value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="GST Rate (%)" value={form.gstRate}
                onChange={(e) => setForm((f) => ({ ...f, gstRate: Number(e.target.value) }))}
                disabled={!form.gstEnabled} sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.gstEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, gstEnabled: e.target.checked }))} />}
                label="GST Enabled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Status" value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                sx={catchyTextFieldSx}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} sx={catchyPrimaryButtonSx}>Save</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
